/**
 * ServiceContentReview
 *
 * Reviews service-generated articles against quality criteria:
 * - Duration mentioned
 * - Itinerary structured (bullet/numbered steps)
 * - Inclusions stated
 * - Cancellation policy visible
 * - Price model clear
 * - No e-commerce vocabulary (SKU, stock, shipping, warranty)
 *
 * Displays a checklist per article with pass/fail for each rule,
 * plus summary stats at top.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Clock,
  ListChecks,
  FileText,
  Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// E-commerce vocabulary that should NOT appear in service articles
const ECOMMERCE_TERMS = [
  'sku', 'stock', 'shipping', 'warranty', 'add to cart',
  'in stock', 'out of stock', 'free shipping', 'return policy',
  'barcode', 'upc', 'ean', 'asin', 'inventory', 'warehouse',
];

interface ServiceArticle {
  id: string;
  title: string;
  service_name: string;
  content_type: string;
  status: string;
  created_at: string;
  updated_at: string;
  content: {
    sections?: Record<string, any>;
    summary?: string;
    [key: string]: any;
  };
}

interface QualityCheck {
  label: string;
  key: string;
  passed: boolean;
  detail?: string;
}

interface ArticleReview {
  article: ServiceArticle;
  checks: QualityCheck[];
  passCount: number;
  totalChecks: number;
  score: number;
}

function evaluateArticle(article: ServiceArticle): ArticleReview {
  const content = article.content || {};
  const sections = content.sections || {};
  const fullText = JSON.stringify(content).toLowerCase();

  const checks: QualityCheck[] = [];

  // 1. Duration mentioned
  const durationPatterns = /\b(\d+\s*(hour|hr|minute|min|day|week)s?|duration|length of|how long|time required)\b/i;
  const hasDuration = durationPatterns.test(fullText);
  checks.push({
    label: 'Duration mentioned',
    key: 'duration',
    passed: hasDuration,
    detail: hasDuration ? 'Duration reference found in content' : 'No duration or time reference found',
  });

  // 2. Itinerary structured (bullet list or numbered steps)
  const hasItinerary = Boolean(
    sections.itinerary ||
    sections.steps ||
    sections.schedule ||
    sections.what_to_expect ||
    sections.process ||
    // Check for numbered/bulleted list patterns in text
    /(\d+\.\s|\*\s|-\s|step\s+\d)/i.test(fullText)
  );
  checks.push({
    label: 'Itinerary structured',
    key: 'itinerary',
    passed: hasItinerary,
    detail: hasItinerary ? 'Structured itinerary or steps found' : 'No structured itinerary or step list found',
  });

  // 3. Inclusions stated
  const hasInclusions = Boolean(
    sections.inclusions ||
    sections.included ||
    sections.whats_included ||
    sections.includes ||
    /\b(included|inclusions|what's included|comes with|package includes)\b/i.test(fullText)
  );
  checks.push({
    label: 'Inclusions stated',
    key: 'inclusions',
    passed: hasInclusions,
    detail: hasInclusions ? 'Inclusions information found' : 'No inclusions or "what\'s included" section found',
  });

  // 4. Cancellation policy visible
  const hasCancellation = /\b(cancellation|cancel|refund|reschedule|no.show|booking change)\b/i.test(fullText);
  checks.push({
    label: 'Cancellation policy visible',
    key: 'cancellation',
    passed: hasCancellation,
    detail: hasCancellation ? 'Cancellation policy reference found' : 'No cancellation or refund policy found',
  });

  // 5. Price model clear
  const hasPricing = /\b(price|pricing|cost|from\s*\$|per\s+person|per\s+group|starting\s+at|free|complimentary)\b/i.test(fullText);
  checks.push({
    label: 'Price model clear',
    key: 'pricing',
    passed: hasPricing,
    detail: hasPricing ? 'Pricing information found' : 'No pricing model or cost information found',
  });

  // 6. No e-commerce vocabulary
  const foundEcommerceTerms = ECOMMERCE_TERMS.filter(term =>
    fullText.includes(term.toLowerCase())
  );
  const noEcommerce = foundEcommerceTerms.length === 0;
  checks.push({
    label: 'No e-commerce vocabulary',
    key: 'no_ecommerce',
    passed: noEcommerce,
    detail: noEcommerce
      ? 'No product-shape phrases found'
      : `Found: ${foundEcommerceTerms.join(', ')}`,
  });

  const passCount = checks.filter(c => c.passed).length;

  return {
    article,
    checks,
    passCount,
    totalChecks: checks.length,
    score: Math.round((passCount / checks.length) * 100),
  };
}

export function ServiceContentReview() {
  const { selectedSite } = useSite();
  const [articles, setArticles] = useState<ServiceArticle[]>([]);
  const [reviews, setReviews] = useState<ArticleReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchArticles = useCallback(async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch service AEO content / articles
      const response = await authFetch(
        `${API_URL}/content-review/queue?siteId=${selectedSite.id}&contentType=service_aeo&limit=100`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch articles: ${response.status}`);
      }
      const data = await response.json();
      const items: ServiceArticle[] = (data.data?.items || data.items || []).map((item: any) => ({
        id: item.id,
        title: item.title || item.service_name || 'Untitled',
        service_name: item.title || item.service_name || '',
        content_type: item.contentType || item.content_type || 'service_aeo',
        status: item.status || 'pending',
        created_at: item.createdAt || item.created_at || '',
        updated_at: item.updatedAt || item.updated_at || '',
        content: item.content || item.aeo_content || {},
      }));
      setArticles(items);
      setReviews(items.map(evaluateArticle));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch service articles');
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Filter reviews
  const filteredReviews = reviews.filter(r => {
    if (statusFilter !== 'all' && r.article.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!r.article.title.toLowerCase().includes(q) &&
          !r.article.service_name.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  // Summary stats
  const totalArticles = filteredReviews.length;
  const avgScore = totalArticles > 0
    ? Math.round(filteredReviews.reduce((sum, r) => sum + r.score, 0) / totalArticles)
    : 0;
  const perfectArticles = filteredReviews.filter(r => r.score === 100).length;
  const checkLabels = ['Duration mentioned', 'Itinerary structured', 'Inclusions stated',
    'Cancellation policy visible', 'Price model clear', 'No e-commerce vocabulary'];
  const checkPassRates = checkLabels.map(label => {
    const passed = filteredReviews.filter(r =>
      r.checks.find(c => c.label === label)?.passed
    ).length;
    return { label, passed, total: totalArticles, rate: totalArticles > 0 ? Math.round((passed / totalArticles) * 100) : 0 };
  });

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to review service content.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Service Content Review</h1>
          <p className="text-muted-foreground mt-1">
            Review service articles against quality criteria
          </p>
        </div>
        <Button variant="outline" onClick={fetchArticles} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Articles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalArticles}</div>
            <p className="text-xs text-muted-foreground">
              {perfectArticles} fully passing
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Quality Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgScore}%</div>
            <Progress value={avgScore} className="h-2 mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pass Rate by Check</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {checkPassRates.filter(c => c.rate >= 80).length}/{checkPassRates.length}
            </div>
            <p className="text-xs text-muted-foreground">
              checks above 80% pass rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-check pass rates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ListChecks className="h-5 w-5" />
            Quality Criteria Pass Rates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {checkPassRates.map(({ label, passed, total, rate }) => (
            <div key={label} className="flex items-center gap-4">
              <div className="w-48 text-sm font-medium truncate">{label}</div>
              <div className="flex-1">
                <Progress value={rate} className="h-3" />
              </div>
              <div className="w-24 text-sm text-right text-muted-foreground">
                {passed}/{total} ({rate}%)
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by service name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Article List with Checklists */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {articles.length === 0
                ? 'No service articles found. Generate service AEO content first.'
                : 'No articles match the current filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredReviews.map((review) => (
            <Card key={review.article.id} className="hover:bg-accent/30 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium truncate">{review.article.title}</h3>
                      <Badge variant={review.article.status === 'approved' ? 'default' : review.article.status === 'rejected' ? 'destructive' : 'secondary'}>
                        {review.article.status}
                      </Badge>
                    </div>
                    {review.article.service_name !== review.article.title && (
                      <p className="text-sm text-muted-foreground truncate">{review.article.service_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Badge
                      variant={review.score === 100 ? 'default' : review.score >= 67 ? 'secondary' : 'destructive'}
                      className="text-sm"
                    >
                      {review.passCount}/{review.totalChecks}
                    </Badge>
                  </div>
                </div>

                {/* Checklist */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {review.checks.map((check) => (
                    <div
                      key={check.key}
                      className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${
                        check.passed
                          ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/30'
                          : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30'
                      }`}
                      title={check.detail}
                    >
                      {check.passed ? (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 flex-shrink-0" />
                      )}
                      <span className="truncate">{check.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
